import '@/App.css';
import { Loader2 } from 'lucide-react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import UnderAttackGate from '@/components/UnderAttackGate';
import { AdminProvider } from '@/contexts/AdminContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { MeProvider } from '@/contexts/MeContext';
import { loginPathFor } from '@/lib/safeNext';
import AdminApp from '@/pages/AdminApp';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import Home from '@/pages/Home';
import LoginPage from '@/pages/LoginPage';
import MemberApp from '@/pages/MemberApp';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SignupPage from '@/pages/SignupPage';

const RequireAuth = ({ children }) => {
  const { authUser, bootLoading } = useAuth();
  const location = useLocation();
  if (bootLoading) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFB612]" />
      </div>
    );
  }
  // Back to this page (path + query, e.g. an email deep link) after sign-in.
  if (!authUser) return <Navigate to={loginPathFor(location)} replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <UnderAttackGate>
          <AuthProvider>
            <MeProvider>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth>
                      <MemberApp />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <MemberApp />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <AdminProvider>
                        <AdminApp />
                      </AdminProvider>
                    </RequireAuth>
                  }
                />
                {/* Username-based referral landing: /:ref → Home (sales
                    page) with that referral pre-resolved. The Home page
                    reads the ref param, surfaces a "Referred by @user"
                    pill, and forwards the ref via ?ref=… on every Sign-Up
                    CTA so the SignupPage picks it up on the next click.
                    Must come BEFORE the wildcard, and AFTER all reserved
                    app routes above. Only matches a single path segment
                    (no slashes), so nested admin/user paths still resolve
                    correctly. */}
                <Route path="/:ref" element={<Home />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster
                theme="dark"
                position="top-right"
                toastOptions={{
                  style: {
                    background: 'rgba(10, 10, 11, 0.95)',
                    color: '#f7f7f8',
                    border: '1px solid rgba(255, 182, 18, 0.32)',
                    fontFamily: 'Inter, sans-serif',
                  },
                }}
              />
            </MeProvider>
          </AuthProvider>
        </UnderAttackGate>
      </BrowserRouter>
    </div>
  );
}

export default App;
